import classNames from 'classnames';
import { SwiperClass, useSwiper } from 'swiper/react';
import ContentNavigation from '@/components/molecules/content-navigation/content-navigation';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './header-carousel-navigation.module.scss';

export interface HeaderCarouselNavigationProps {
  /** The class name to apply */
  className?: string;
  /** The current index */
  currentIndex?: number;
  /** Indicates whether autoplay is active */
  isPlaying?: boolean;
  /** An event that gets triggered when pausing */
  onPause?: () => void;
  /** An event that gets triggered when playing */
  onPlay?: () => void;
  /** The total page count */
  pageCount?: number;
  /** Reference to the preview swiper */
  previewSwiper?: SwiperClass;
  /** Timer duration in ms */
  timerDuration?: number;
  /** Variant of the header carousel navigation */
  variant?: string;
}

const HeaderCarouselNavigation = ({
  className,
  currentIndex,
  isPlaying = false,
  onPause,
  onPlay,
  pageCount,
  previewSwiper,
  timerDuration = 5000,
  variant,
}: HeaderCarouselNavigationProps) => {
  const swiper = useSwiper();
  const isTablet = useWindowSize('tablet');

  const handleTogglePlay = () => {
    if (isPlaying) {
      swiper.autoplay.pause();
      previewSwiper?.autoplay.pause();
      onPause?.();
    } else {
      swiper.autoplay.start();
      previewSwiper?.autoplay.start();
      onPlay?.();
    }
  };

  const handleClickNext = () => {
    swiper.autoplay.pause();
    previewSwiper?.autoplay.pause();
    onPause?.();
    swiper.slideNext();
  };

  const handleClickPrevious = () => {
    swiper.autoplay.pause();
    previewSwiper?.autoplay.pause();
    onPause?.();
    swiper.slidePrev();
  };

  return (
    <div className={classNames(styles.component, className)}>
      <ContentNavigation
        className={styles.navigation}
        currentIndex={currentIndex}
        isPlaying={isPlaying}
        onClickNext={handleClickNext}
        onClickPrevious={handleClickPrevious}
        onTogglePlay={handleTogglePlay}
        pageCount={pageCount}
      />

      {isTablet && variant === 'secondary' && (
        <div className={styles.progressBar}>
          <div
            key={currentIndex}
            className={classNames(styles.progress, {
              [styles.isPaused]: !isPlaying,
            })}
            style={{ animationDuration: `${timerDuration}ms` }}
          />
        </div>
      )}
    </div>
  );
};

HeaderCarouselNavigation.displayName = 'HeaderCarouselNavigation';

export default HeaderCarouselNavigation;
